import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, Circle } from "lucide-react";
import PageIntro from "@/components/PageIntro";
import Reveal, { RevealGroup, RevealItem } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORK = [
  {
    t: "Standards alignment",
    d: "Mapping the governed handoff and receipt contract to established message and record standards — ISO 20022 for value movement and FHIR for health records — so existing systems consume outcomes natively.",
  },
  {
    t: "Technical challenge participation",
    d: "Engaging with structured technical challenges and evaluation programmes to test the receipt contract, custody boundary and authority model against real constraints.",
  },
  {
    t: "Validation & acceptance",
    d: "Operational acceptance evidence produced through Cierge Lab sandboxes and role-network pilots — acceptance criteria, receipt models and cross-device proof.",
  },
  {
    t: "Publications & method",
    d: "Documenting the method: the Control Decision Envelope, the CAR model and the public-safe receipt projection, as a reusable operational-governance baseline.",
  },
];

const MATRIX = [
  {
    k: "May expose publicly",
    items: ["Receipt hash", "Attestation reference", "Signer reference", "Node timestamp", "Verification status", "Selected shared evidence refs", "Withheld evidence count"],
    tone: "gold",
  },
  {
    k: "Must not expose",
    items: ["Node credential & auth header", "Full signed-receipt envelope", "Raw personal data", "Withheld assets", "Payment details", "Safeguarding notes", "Raw prompts"],
    tone: "ink",
  },
];

function StatusBadge() {
  const [state, setState] = useState({ status: "checking", message: "Checking node status…" });
  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/katlas/status`)
      .then((r) => active && setState(r.data))
      .catch(() => active && setState({ status: "unreachable", message: "KATLAS node unreachable" }));
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="inline-flex items-center gap-3 border border-ink/20 px-4 py-2.5" data-testid="katlas-status">
      <Circle
        className={`h-2.5 w-2.5 ${state.status === "configured" ? "fill-teal text-teal" : "fill-gold text-gold"}`}
        strokeWidth={0}
      />
      <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink/70" data-testid="katlas-status-message">
        {state.message}
      </span>
    </div>
  );
}

export default function Evidence() {
  return (
    <div data-testid="evidence-page">
      <PageIntro
        index="/evidence"
        eyebrow="Evidence"
        titleLines={["Standards, validation", "and the receipt", "contract."]}
        lead="We evidence the engineering architecture through standards alignment, technical challenge participation and operational acceptance — not through unverifiable claims. Named deployments and measurable outcomes will be published here as they are validated."
        meta={["Standards", "Validation", "Publications"]}
      />

      <section className="border-b border-ink/12 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <p className="max-w-lg font-sans text-base leading-relaxed text-ink/70">
              This build runs against the live node configuration with the
              production credential held backend-only. Current readiness:
            </p>
            <StatusBadge />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <RevealGroup className="grid grid-cols-1 gap-px border border-ink/15 bg-ink/15 md:grid-cols-2">
            {WORK.map((w) => (
              <RevealItem key={w.t} className="bg-stone p-8 md:p-10">
                <h3 className="font-serif text-2xl text-ink">{w.t}</h3>
                <p className="mt-4 font-sans text-sm leading-relaxed text-ink/70">{w.d}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      <section className="border-t border-ink/12 bg-stone py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 flex items-center gap-4">
            <span className="font-mono text-xs tracking-[0.24em] text-gold">§ boundary</span>
            <span className="h-px w-10 bg-ink/25" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink/45">
              Public-safe receipt
            </span>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {MATRIX.map((col) => (
              <Reveal key={col.k} className={`border p-8 ${col.tone === "gold" ? "border-gold/40" : "border-ink/20"}`}>
                <h3 className={`font-mono text-[11px] uppercase tracking-[0.2em] ${col.tone === "gold" ? "text-gold" : "text-ink/60"}`}>
                  {col.k}
                </h3>
                <ul className="mt-6 space-y-3">
                  {col.items.map((it) => (
                    <li key={it} className="flex items-baseline gap-3 border-b border-ink/10 pb-3 font-mono text-sm text-ink/75">
                      <span className={col.tone === "gold" ? "text-gold" : "text-ink/30"}>—</span>
                      {it}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10 max-w-2xl">
            <p className="font-mono text-xs leading-relaxed text-ink/60">
              KATLAS attests the governed action using metadata, roles, permissions
              and evidence references. Raw private data and withheld assets are not
              sent to the node.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-ink/12 py-20 md:py-28" data-testid="trust-architecture">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-10 flex items-center gap-4">
            <span className="font-mono text-xs tracking-[0.24em] text-teal">§ trust</span>
            <span className="h-px w-10 bg-ink/25" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink/45">
              Trust &amp; architecture
            </span>
          </div>
          <p className="max-w-2xl font-sans text-base leading-relaxed text-ink/70 md:text-lg">
            DPIA-supporting material. These artefacts help assess data protection and
            architecture; a website document does not, by itself, constitute a completed
            customer DPIA.
          </p>
          <Link
            to="/technical-appendix"
            data-testid="evidence-appendix-link"
            className="group mt-6 inline-flex items-center gap-2 font-sans text-sm text-teal transition-colors hover:text-ink"
          >
            Read the technical appendix
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.75} />
          </Link>
          <div className="mt-10 grid grid-cols-1 gap-px border border-ink/15 bg-ink/15 sm:grid-cols-2 lg:grid-cols-3">
            {[
              "Logical architecture diagram",
              "Data-flow diagram",
              "Trust-boundary diagram",
              "Evidence shared / withheld matrix",
              "Privacy & data-minimisation note",
              "Bounded AI rules",
              "Receipt public-field whitelist",
              "Deployment assurance controls",
            ].map((t) => (
              <div key={t} className="bg-stone p-7">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/45">
                  Placeholder
                </p>
                <h3 className="mt-3 font-serif text-lg text-ink">{t}</h3>
                <p className="mt-2 font-sans text-sm text-ink/60">
                  Prepared for founder review — not yet published.
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal className="flex flex-col items-start justify-between gap-6 border border-ink/15 p-8 md:flex-row md:items-center md:p-12">
            <p className="max-w-xl font-serif text-xl leading-snug text-ink md:text-2xl">
              Bring a bounded problem and we will produce acceptance evidence for it.
            </p>
            <Link
              to="/company#contact"
              className="group inline-flex shrink-0 items-center gap-2 bg-teal px-6 py-3.5 font-sans text-sm font-medium text-stone transition-colors duration-300 hover:bg-ink"
            >
              Bring us a journey
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
